import * as ProDuotoneSvgIcons from '@fortawesome/pro-duotone-svg-icons'
import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import * as React from 'react'
import { Link, SetURLSearchParams, useSearchParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { EnvelopeIcon } from 'src/assets/icons/customIcons/Envelope'
import { getStudentProgramByDegree } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { useUserStateMutateOnMount } from 'src/hooks/userState'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import ErrorElement from 'src/views/components/ErrorElement'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import { SortButtons } from 'src/views/components/SortButtons'
import UserPhoto from 'src/views/components/UserPhoto'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { SearchCancelButtons } from 'src/views/components/forms/formik/SearchCancelButtons'
import SelectInput, { type Option } from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import UserEmulationButton from './AdministratorUsersEmulate/UserEmulateButton'
import useEmulateUser from './AdministratorUsersEmulate/useEmulateUser'
import ClinicalInternshipReferenceButton from './reference/ClinicalInternshipReferenceButton'

const DEFAULT_PAGE = '1'
const DEFAULT_PER_PAGE = '10'
const PER_PAGE_OPTIONS = [
  { value: 10, label: 10 },
  { value: 20, label: 20 },
  { value: 50, label: 50 },
  { value: 1000, label: 1000 },
]

interface StudentsFormValues {
  readonly query: string
  readonly degree: string
  readonly semId: string
  readonly facid: string
  readonly minorFacId: string
  readonly status: string | readonly string[]
  readonly userSem: string
}

const FORM_KEYS: (keyof StudentsFormValues)[] = ['query', 'degree', 'semId', 'facid', 'minorFacId', 'status', 'userSem']

function getStudentProfile(profiles: Api.AdministratorUser['profiles']): Api.UserProfileStudent | undefined {
  return profiles?.find(
    (p): p is Api.UserProfileStudent => p.type === 'UserProfileStudent' || p.type === 'UserProfileStudentHistory'
  )
}

export default function AdministratorUsersStudentsPage(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={`${t('system:users')} - ${t('student:students')}`}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const locale = useLocale()
  useUserStateMutateOnMount()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const definitionParams = React.useMemo(() => ({ headers }), [headers])

  const definitionSwrOptions = React.useMemo(
    () =>
      ({
        revalidateOnFocus: false,
        revalidateIfStale: false,
      }) as const,
    []
  )

  const { data: studentDefinitions } = useApi({
    endpoint: Api.getAdministratorDefinitionsStudents,
    params: definitionParams,
    ...definitionSwrOptions,
  })

  const activeSemester = useActiveSemester()

  const breadcrumbsItems = React.useMemo(
    () => [
      {
        page: `${t('system:users')} - ${t('student:students')}`,
        path: `/${locale}/administrator/users/students`,
      },
    ],
    [locale, t]
  )

  return (
    <Section
      title={t('system:users')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProDuotoneSvgIcons.faUsers} />}
    >
      <FilterForm studentDefinitions={studentDefinitions} activeSemester={activeSemester} />
      <SuspenseWrapper>
        <UsersTable studentDefinitions={studentDefinitions} activeSemester={activeSemester} />
      </SuspenseWrapper>
    </Section>
  )
}

function FilterForm({
  studentDefinitions,
  activeSemester,
}: {
  readonly studentDefinitions: Api.AdministratorDefinitionStudent
  readonly activeSemester: Api.Semester | null
}): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const t = useTranslatable()

  const query = searchParams.get('query') || ''
  const degree = searchParams.get('degree') || ''
  const semId = searchParams.get('semId') || (activeSemester?.id != null ? String(activeSemester.id) : '')
  const facid = searchParams.get('facid') || ''
  const minorFacId = searchParams.get('minorFacId') || ''
  const statusParam = searchParams.get('status') || ''
  const userSem = searchParams.get('userSem') || ''

  const statusInitial = statusParam
    ? statusParam
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : ''

  const initialValues: StudentsFormValues = {
    query,
    degree,
    semId,
    facid,
    minorFacId,
    status: statusInitial,
    userSem,
  }

  const headers = useAuthenticatedHeaders()

  const { data: degrees } = useApi({
    endpoint: Api.getRegimeDegrees,
    params: { headers },
  })
  const { data: faculties } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: { headers },
  })
  const { data: semesters, isValidating: isSemestersValidating } = useApi({
    endpoint: Api.getSemesters,
    params: { headers },
  })

  const handleFormSubmit = React.useCallback(
    (values: StudentsFormValues) => {
      setSearchParams((params) => {
        FORM_KEYS.forEach((key) => {
          const raw = values[key]
          const value: string =
            key === 'status'
              ? Array.isArray(raw)
                ? (raw as string[]).filter(Boolean).join(',')
                : (raw as string) || ''
              : ((raw as string) ?? '')
          if (value) params.set(key, value)
          else params.delete(key)
        })
        params.set('page', DEFAULT_PAGE)
        return params
      })
    },
    [setSearchParams]
  )

  const degreeOptions = React.useMemo(() => {
    if (!degrees) return [{ value: '', label: t('common:any') }]
    return [{ value: '', label: t('common:any') }, ...degrees.map((d) => ({ value: d.id, label: d.name }))]
  }, [degrees, t])

  const facultyOptions = React.useMemo(() => {
    if (!faculties) return [{ value: '', label: t('common:any') }]
    return [{ value: '', label: t('common:any') }, ...faculties.map((f) => ({ value: f.id, label: f.name }))]
  }, [faculties, t])

  const statusOptions = React.useMemo(() => {
    const statuses = (studentDefinitions?.statuses ?? []) as readonly Option[]
    return [{ value: '__all__', label: t('common:all') }, ...statuses]
  }, [studentDefinitions?.statuses, t])

  const userSemOptions = (studentDefinitions?.semesters ?? []) as readonly Option[]

  const semesterOptions = React.useMemo(() => {
    if (!semesters) return [{ value: '', label: t('common:any') }]
    return [{ value: '', label: t('common:any') }, ...semesters.map((s) => ({ value: s.id, label: s.name }))]
  }, [semesters, t])

  return (
    <Form
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      classNames={{ form: 'w-full px-2 mt-3 mb-1' }}
      enableReinitialize
    >
      <fieldset className="flex w-full flex-col justify-between">
        <FormError />
        <div className="mb-3 flex w-full flex-wrap justify-between gap-2">
          <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
            <TextInput
              type="text"
              label={t('common:enter_your_keyword')}
              name="query"
              placeholder={t('common:enter_your_keyword')}
            />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput label={t('person:degree')} name="degree" options={degreeOptions} />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput
              label={t('common:semester')}
              name="semId"
              options={semesterOptions}
              disabled={isSemestersValidating}
              getOptionLabel={(option) =>
                option.label + (option.value === activeSemester?.id ? t('common:current') : '')
              }
            />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput label={t('faculty:major_faculty')} name="facid" options={facultyOptions} />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput label={t('faculty:minor_faculty')} name="minorFacId" options={facultyOptions} />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput
              label={t('common:status')}
              name="status"
              options={statusOptions}
              isMulti
              storeValueOnly
              closeMenuOnSelect={false}
              selectAllOptionValue="__all__"
            />
          </div>
          <div className="w-full min-w-[100px] md:w-[49%]">
            <SelectInput label={t('student:student_semester')} name="userSem" options={userSemOptions} />
          </div>
        </div>
        <FilterActions
          setSearchParams={setSearchParams}
          currentQuery={query}
          currentDegree={degree}
          currentSemId={semId}
          currentFacId={facid}
          currentMinorFacId={minorFacId}
          currentStatus={statusParam}
          currentUserSem={userSem}
        />
      </fieldset>
    </Form>
  )
}

function FilterActions({
  setSearchParams,
  currentQuery,
  currentDegree,
  currentSemId,
  currentFacId,
  currentMinorFacId,
  currentStatus,
  currentUserSem,
}: {
  setSearchParams: SetURLSearchParams
  currentQuery: string
  currentDegree: string
  currentSemId: string
  currentFacId: string
  currentMinorFacId: string
  currentStatus: string
  currentUserSem: string
}): JSX.Element {
  const formik = useFormikContext<StudentsFormValues>()
  const statusForCompare = Array.isArray(formik.values.status)
    ? formik.values.status.join(',')
    : formik.values.status || ''
  const isDirty =
    formik.values.query !== currentQuery ||
    formik.values.degree !== currentDegree ||
    formik.values.semId !== currentSemId ||
    formik.values.facid !== currentFacId ||
    formik.values.minorFacId !== currentMinorFacId ||
    statusForCompare !== currentStatus ||
    formik.values.userSem !== currentUserSem
  const canCancel =
    !currentQuery &&
    !currentDegree &&
    !currentSemId &&
    !currentFacId &&
    !currentMinorFacId &&
    !currentStatus &&
    !currentUserSem
  return (
    <SearchCancelButtons
      isDirty={isDirty}
      canCancel={canCancel}
      onCancel={() => {
        setSearchParams({
          query: '',
          degree: '',
          semId: '',
          facid: '',
          minorFacId: '',
          status: '',
          userSem: '',
          page: DEFAULT_PAGE,
          perPage: DEFAULT_PER_PAGE,
        })
        formik.resetForm()
        formik.submitForm()
      }}
    />
  )
}

function UsersTable({
  studentDefinitions,
  activeSemester,
}: {
  readonly studentDefinitions: Api.AdministratorDefinitionStudent
  readonly activeSemester: Api.Semester | null
}): JSX.Element | null {
  const emulateUser = useEmulateUser()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  useUserStateMutateOnMount()
  const t = useTranslatable()
  const theme = useTheme()

  const [searchParams] = useSearchParams()

  const query = searchParams.get('query') || ''
  const degree = searchParams.get('degree') || ''
  const semId = searchParams.get('semId') || (activeSemester?.id != null ? String(activeSemester.id) : '')
  const facid = searchParams.get('facid') || ''
  const minorFacId = searchParams.get('minorFacId') || ''
  const statusParam = searchParams.get('status') || ''
  const userSem = searchParams.get('userSem') || ''
  const page = searchParams.get('page') || DEFAULT_PAGE
  const perPage = searchParams.get('perPage') || DEFAULT_PER_PAGE
  const sortBy = searchParams.get('sortBy') || 'firstName:asc'

  const { data: usersResponse, error } = useApi({
    endpoint: Api.getAdministratorStudentUsers,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          sortBy,
          page,
          perPage,
          filters: {
            search: query || null,
            degree: degree || null,
            semId: semId || null,
            facId: facid || null,
            minorFacId: minorFacId || null,
            status: statusParam || null,
            userSem: userSem || null,
          },
        },
      }),
      [headers, sortBy, page, perPage, query, degree, semId, facid, minorFacId, statusParam, userSem]
    ),
  })

  const getEduStatus = (eduStatus: string | null | undefined) => {
    const label = studentDefinitions?.statuses?.find((s) => s.value === eduStatus)?.label
    return label ?? '—'
  }

  if (error != null) {
    return <ErrorElement errorProp={error as base.CommonError} />
  }
  if (usersResponse?.data != null && usersResponse.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }
  if (usersResponse?.meta == null) return null

  const users = usersResponse.data ?? []
  const pagination = usersResponse.meta.pagination

  return (
    <div className="w-full py-3">
      <PaginationTop pagination={pagination} showTotalCount options={{ perPageOptions: PER_PAGE_OPTIONS }} />
      <Table.Table className="mt-7 w-full min-w-[1140px] table-auto">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="w-[80px] min-w-[80px]">
              <div className="flex items-center gap-2">
                <span>ID</span>
                <SortButtons sortField="id" />
              </div>
            </Table.Th>
            <Table.Th className="min-w-[90px]">
              <span>{t('person:profile_id')}</span>
            </Table.Th>
            <Table.Th className="flex w-[200px] min-w-[200px] items-center">
              <div className="flex items-center gap-2">
                <span>
                  {t('person:lastname')}/{t('person:firstname')}
                </span>
                <SortButtons sortField="firstName" />
              </div>
            </Table.Th>
            <Table.Th className="min-w-[120px]">
              <div className="flex items-center gap-2">
                <span>{t('person:personal_number')}</span>
                <SortButtons sortField="personalNumber" />
              </div>
            </Table.Th>
            <Table.Th className="min-w-[160px]">
              <span>{t('person:e_mail')}</span>
            </Table.Th>
            <Table.Th className="min-w-[100px]">
              <span>{t('person:degree')}</span>
            </Table.Th>
            <Table.Th className="min-w-[80px]">
              <span>{t('common:semester')}</span>
            </Table.Th>
            <Table.Th className="min-w-[100px]">
              <span>{t('common:choose_regime')}</span>
            </Table.Th>
            <Table.Th className="min-w-[80px]">
              <span>{t('common:status')}</span>
            </Table.Th>
            <Table.Th className="min-w-[130px]" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {users.map((user, index) => {
            const studentProfile = getStudentProfile(user.profiles)
            return (
              <Table.Tr key={`${studentProfile?.id}-${index}`}>
                <Table.Td className="min-w-[80px] rounded-l-lg">{user.id}</Table.Td>
                <Table.Td className="min-w-[90px]">{studentProfile?.id ?? '—'}</Table.Td>
                <Table.Td className="min-w-[200px] align-middle">
                  <div className="flex items-center">
                    <UserPhoto user={user} />
                    <span className="ml-2">{user.fullName}</span>
                  </div>
                </Table.Td>
                <Table.Td className="min-w-[120px]">{user.personalNumber}</Table.Td>
                <Table.Td className="min-w-[160px]">{user.email ?? '—'}</Table.Td>
                <Table.Td className="min-w-[100px]">
                  {studentProfile?.degree != null ? getStudentProgramByDegree(studentProfile.degree) : '—'}
                </Table.Td>
                <Table.Td className="min-w-[80px]">{studentProfile?.semester ?? '—'}</Table.Td>
                <Table.Td className="min-w-[100px]">
                  {studentProfile?.chooseRegime ? t('common:yes') : t('common:no')}
                </Table.Td>
                <Table.Td className="min-w-[80px]">{getEduStatus(studentProfile?.eduStatus)}</Table.Td>
                <Table.Td className="min-w-[130px]">
                  <div className="flex items-center">
                    <Link
                      to={`/${locale}/messages/compose/${user.uid}`}
                      className="flex rounded-[18px] p-[10px] text-xs"
                    >
                      <span className="w-[18px]">
                        <EnvelopeIcon />
                      </span>
                    </Link>
                    {studentProfile != null && (
                      <>
                        <Tooltip
                          id={`${user.id}-addCourseTooltip`}
                          place="top"
                          variant={theme === 'dark' ? 'dark' : 'light'}
                          className="mb-1 p-1"
                        >
                          {t(['კურსების მართვა', 'Manage courses'])}
                        </Tooltip>
                        <Link
                          to={`/${locale}/administrator/users/students/${user.uid}/${studentProfile.id}/choices`}
                          className="flex rounded-[18px] p-[10px] text-xs"
                          data-tooltip-id={`${user.id}-addCourseTooltip`}
                        >
                          <span className="w-[18px]">
                            <FontAwesomeIcon icon={ProRegularSvgIcons.faList} />
                          </span>
                        </Link>
                      </>
                    )}
                    {studentProfile != null && (
                      <ClinicalInternshipReferenceButton user={user} studentProfile={studentProfile} semId={semId} />
                    )}
                    <UserEmulationButton user={user} emulateUser={emulateUser} />
                  </div>
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table.Table>
      <PaginationBottom pagination={pagination} options={{ perPageOptions: PER_PAGE_OPTIONS }} />
    </div>
  )
}
